import cn from 'classnames';
import { Drawer, Form, Layout, Localization } from 'connex-cds';
import { find, omit } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import {
  useCompanySetup,
  useProductSetup,
  useReasonCodeSetup,
  useVehicleSetup,
  useVehicleTypeSetup,
} from '../../../MasterDataProvider';
import { useTicketContext } from '../../../TicketContext';
import style from './form-style';

const Styled = styled.div`
  ${style}
`;

const { Row, Container } = Layout;

const sourceProbe = { id: 'PROBE' };
const sourceDriver = { id: 'DRIVER' };
const sources = [sourceProbe, sourceDriver];

const locationAtJob = { id: 'at-job' };
const locationAtPlant = { id: 'at-plant' };
const locations = [locationAtJob, locationAtPlant];

const translateWithFallback = (items, config, translateFn) => {
  try {
    const result = translateFn(items, config);
    return result;
  } catch {
    return null;
  }
};

export const FormSection = ({ className, isMobile = false, disableAddButton = false }) => {
  const {
    Components: {
      Reason,
      Quantity,
      ConcreteOnTruck,
      Time,
      WaterProduct,
      CancelButton,
      SaveButton,
      ResetButton,
      DriverDidNotAdd,
      WaterProductSource,
      WaterLocation,
    },
    values,
    setFieldValue,
    mode,
  } = Form.useFormContext();

  const { translateObjects } = Localization.useTranslateObjects();

  const { closeDrawer } = Drawer.useDrawerContext();
  const saveButtonAnchorRef = React.useRef(null);
  const prevDriverDidNotAddRef = React.useRef(undefined);
  const [productUom, setProductUom] = React.useState();
  const [concreteOnTruckUom, setConcreteOnTruckUom] = React.useState();

  React.useEffect(() => {
    if (!values?.waterProduct || values?.waterProduct?.mobileTicket?.isReasonFieldEnabled === false) {
      setFieldValue('reason', undefined);
    }
    if (!values?.waterProduct || values?.waterProduct?.mobileTicket?.isConcreteOnTruckFieldEnabled === false) {
      setFieldValue('concreteOnTruck', undefined);
    }
  }, [setFieldValue, values?.waterProduct]);

  const { ticket } = useTicketContext();

  const companySetup = useCompanySetup();
  const vehicleSetup = useVehicleSetup();
  const reasonCodeSetup = useReasonCodeSetup();
  const productSetup = useProductSetup();
  const vehicleTypeSetup = useVehicleTypeSetup();

  const primaryLineItem = React.useMemo(() => find(ticket?.lineItems, { isPrimary: true }), [ticket?.lineItems]);

  const busy = React.useMemo(() => {
    return companySetup.isLoading || vehicleSetup.isLoading || reasonCodeSetup.isLoading || productSetup.isLoading;
  }, [companySetup.isLoading, productSetup.isLoading, reasonCodeSetup.isLoading, vehicleSetup.isLoading]);

  React.useEffect(() => {
    setProductUom(values?.waterProduct?.uomCode);
  }, [values?.waterProduct]);

  const vehicleType = React.useMemo(() => {
    const targetVehicle = find(vehicleSetup.data, { id: ticket?.vehicleId });
    const targetVehicleType = find(vehicleTypeSetup.data, { crn: targetVehicle?.vehicleType?.vehicleTypeRef });
    return targetVehicleType?.id;
  }, [ticket?.vehicleId, vehicleSetup.data, vehicleTypeSetup.data]);

  const reasonsOptions = React.useMemo(() => {
    if (!reasonCodeSetup?.data) {
      return [];
    }

    const reasons = reasonCodeSetup?.data
      ?.filter?.(d => d.type === 'WATER')
      ?.filter?.(reason => reason.status === 'ACTIVE')
      ?.filter?.(reason =>
        companySetup?.data?.isMulticountry === true
          ? reason?.countries?.includes(ticket?.origin?.address?.countryCode) || !reason?.countries?.length
          : reason
      )
      ?.filter?.(reason => reason?.vehicleTypes?.includes(vehicleType) || !reason?.vehicleTypes?.length);

    const reasonCodes = translateWithFallback(
      reasons,
      {
        getStringId: x => `SETUP_REASON-CODE_${x}`.replace(/\s/gi, '_').toUpperCase(),
        getPath: 'id',
        setPath: 'label',
        defaultMessagePath: 'description',
      },
      translateObjects
    );

    if (!reasonCodes) {
      return reasons;
    }

    return reasonCodes.map(reason => ({ ...reason, description: reason?.label ?? reason?.description }));
  }, [
    companySetup?.data?.isMulticountry,
    reasonCodeSetup?.data,
    ticket?.origin?.address?.countryCode,
    vehicleType,
    translateObjects,
  ]);

  const waterOptions = React.useMemo(() => {
    const waterProductType = companySetup?.data?.waterProductType?.id;
    if (waterProductType) {
      if (!productSetup?.data) {
        return [];
      }

      const products = productSetup?.data
        ?.filter?.(d => d.type === waterProductType)
        ?.filter?.(product => product.status === 'ACTIVE')
        ?.filter?.(product =>
          companySetup?.data?.isMulticountry === true
            ? product?.countries?.includes(ticket?.origin?.address?.countryCode) || !product?.countries?.length
            : product
        )
        ?.filter?.(product => product?.vehicleTypes?.includes(vehicleType) || !product?.vehicleTypes?.length);

      const waterProducts = translateWithFallback(
        products,
        {
          getStringId: x => `SETUP_PRODUCT_${x}`.replace(/\s/gi, '_').toUpperCase(),
          getPath: 'id',
          setPath: 'label',
          defaultMessagePath: 'name',
        },
        translateObjects
      );

      if (!waterProducts) {
        return products;
      }

      return waterProducts.map(product => ({
        ...product,
        name: product?.label ?? product?.name ?? product?.description,
      }));
    }
    return [];
  }, [
    companySetup?.data?.isMulticountry,
    companySetup?.data?.waterProductType?.id,
    productSetup?.data,
    ticket?.origin?.address?.countryCode,
    vehicleType,
    translateObjects,
  ]);

  React.useEffect(() => {
    const defaultWaterRef = companySetup?.data?.defaultWaterProduct?.productRef;
    const defaultWater = find(productSetup?.data, { crn: defaultWaterRef });
    if (!values?.waterProduct?.productRef && defaultWater) {
      setFieldValue('waterProduct', { ...omit(defaultWater, 'crn'), productRef: defaultWater.crn });
    }
  }, [
    companySetup?.data?.defaultWaterProduct?.productRef,
    productSetup?.data,
    setFieldValue,
    values?.waterProduct,
    waterOptions,
  ]);

  React.useEffect(() => {
    setConcreteOnTruckUom(primaryLineItem?.quantity?.uomCode);
  }, [primaryLineItem?.quantity?.uomCode]);

  const mobileTicketConfig = React.useMemo(() => {
    const targetItem = find(productSetup?.data, { crn: values?.waterProduct?.productRef });
    return targetItem?.mobileTicket;
  }, [productSetup?.data, values?.waterProduct?.productRef]);

  const disabled = React.useMemo(() => {
    return disableAddButton || values?.driverDidNotAdd;
  }, [values?.driverDidNotAdd, disableAddButton]);

  const isDriverAllowedToEditWater = React.useMemo(() => {
    return companySetup?.data?.isDriverAllowedToEditWater !== false;
  }, [companySetup?.data?.isDriverAllowedToEditWater]);

  const waterFieldsDisabled = React.useMemo(() => {
    // If driver is not allowed to edit water and this is not edit mode,
    // disable all water fields including location
    if (isDriverAllowedToEditWater === false && mode !== 'edit') {
      return true;
    }
    return disabled;
  }, [isDriverAllowedToEditWater, mode, disabled]);

  const driverDidNotAddReadonly = React.useMemo(() => {
    // Make driverDidNotAdd readonly when water is from PROBE or DCM and driver is not allowed to edit water
    const isFromProbeOrDcm = values?.waterProductSource?.id === 'PROBE' || values?.waterProductSource?.id === 'DCM';
    return isFromProbeOrDcm && isDriverAllowedToEditWater === false;
  }, [values?.waterProductSource?.id, isDriverAllowedToEditWater]);

  const productDisabled = React.useMemo(() => {
    return values?.suggestedToDriver || values?.driverDidNotAdd;
  }, [values?.driverDidNotAdd, values?.suggestedToDriver]);

  React.useEffect(() => {
    if (values?.driverDidNotAdd) {
      setFieldValue('quantity', undefined);
      setFieldValue('reason', undefined);
      setFieldValue('concreteOnTruck', undefined);
      setFieldValue('time', undefined);
      setFieldValue('waterLocation', undefined);
    }
  }, [setFieldValue, ticket?.lineItems, values?.driverDidNotAdd, values?.item?.productRef]);

  React.useEffect(() => {
    if (values?.waterProduct?.mobileTicket?.isReasonFieldEnabled && reasonsOptions?.length === 1) {
      const [firstReason] = reasonsOptions;
      const { crn, ...firstReasonProps } = firstReason;
      setFieldValue('reason', { ...firstReasonProps, reasonRef: crn });
    }
  }, [reasonsOptions, values?.waterProduct, setFieldValue]);

  const defaultReason = React.useMemo(() => {
    if (reasonsOptions.some(opt => opt.isDefault)) {
      return reasonsOptions.filter(opt => opt.isDefault);
    }
    return null;
  }, [reasonsOptions]);

  React.useEffect(() => {
    if (defaultReason && !values?.reason) {
      const [option] = defaultReason;
      const { crn, ...defaultReasonProps } = option;
      setFieldValue('reason', { ...defaultReasonProps, reasonRef: crn });
    }
  }, [defaultReason, setFieldValue, values?.reason]);

  const showSource = React.useMemo(
    () => companySetup?.data?.isWaterSourceDcm || companySetup?.data?.isWaterSourceProbe,
    [companySetup?.data?.isWaterSourceDcm, companySetup?.data?.isWaterSourceProbe]
  );

  const showWaterLocation = React.useMemo(
    () => companySetup?.data?.isDotEnabled === true || companySetup?.data?.isWaterLocationTrackingEnabled === true,
    [companySetup?.data?.isDotEnabled, companySetup?.data?.isWaterLocationTrackingEnabled]
  );

  const sourceOptions = React.useMemo(() => {
    return translateObjects(sources, {
      getPath: 'id',
      setPath: 'description',
    });
  }, [translateObjects]);

  React.useEffect(() => {
    const [firstEvent] = ticket?.waterAddedEvents ?? [];

    if (!values?.waterProductSource) {
      setFieldValue('waterProductSource', sourceDriver);
    } else if (values?.waterProductSource?.id === sourceProbe.id) {
      setFieldValue('waterProductSource', sourceProbe);
    } else if (firstEvent?.quantity?.value !== values?.quantity?.value) {
      setFieldValue('waterProductSource', sourceDriver);
    } else if (firstEvent?.source) {
      setFieldValue('waterProductSource', { id: firstEvent?.source });
    } else {
      setFieldValue('waterProductSource', sourceDriver);
    }
  }, [setFieldValue, ticket?.waterAddedEvents, values?.quantity]);

  React.useEffect(() => {
    if (mobileTicketConfig?.isTimeFieldEnabled && !values?.driverDidNotAdd && !values?.time) {
      const now = new Date();
      setFieldValue('time', now.toISOString());
    }
  }, [mobileTicketConfig?.isTimeFieldEnabled, values?.driverDidNotAdd, values?.time, setFieldValue]);

  const locationsSource = React.useMemo(() => {
    return translateObjects(locations, {
      getPath: 'id',
      setPath: 'description',
    });
  }, [translateObjects]);

  React.useEffect(() => {
    if (values?.driverDidNotAdd && companySetup?.data?.isWaterMandatory) {
      setFieldValue('quantity.value', '0');
    }
  }, [values?.driverDidNotAdd, setFieldValue, companySetup?.data?.isWaterMandatory]);

  React.useEffect(() => {
    if (mode !== 'edit') {
      prevDriverDidNotAddRef.current = values?.driverDidNotAdd;
      return;
    }

    const isChecked = values?.driverDidNotAdd === true;
    const prev = prevDriverDidNotAddRef.current;
    prevDriverDidNotAddRef.current = isChecked;

    if (!isChecked || prev === true) {
      return;
    }
    if (prev === undefined && isChecked) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const root = saveButtonAnchorRef.current;
      if (!root) {
        return;
      }
      const target =
        root.querySelector?.('[data-testid="water-added-event-save"]') ||
        root.querySelector?.('button:not([disabled])');
      target?.click?.();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [values?.driverDidNotAdd, mode]);

  return (
    <Styled className={cn('form-section add-water-form-section', className)}>
      <Container flex={1}>
        <div className="form">
          <div className="row-1">
            <WaterProduct options={waterOptions} showSearch={false} disabled={productDisabled || waterFieldsDisabled} />
            {mobileTicketConfig?.isReasonFieldEnabled && (
              <Reason options={reasonsOptions} busy={busy} showSearch={false} disabled={waterFieldsDisabled} />
            )}
          </div>
          <div className={cn('row-2', { disabled: waterFieldsDisabled })}>
            <div className="left">
              <Quantity uomCode={productUom} disabled={waterFieldsDisabled} />
              {mobileTicketConfig?.isConcreteOnTruckFieldEnabled && (
                <ConcreteOnTruck uomCode={concreteOnTruckUom} disabled={waterFieldsDisabled} />
              )}
            </div>
            {mobileTicketConfig?.isTimeFieldEnabled && <Time disabled={waterFieldsDisabled} />}
          </div>
          {showSource && (
            <div className="row-3">
              <div className="left" />
              <div className="right">
                <WaterProductSource options={sourceOptions} disabled={true} />
              </div>
            </div>
          )}
          {mode === 'edit' && (
            <div className="row-3">
              <DriverDidNotAdd disabled={driverDidNotAddReadonly} />
            </div>
          )}
          {showWaterLocation && (
            <div>
              <WaterLocation options={locationsSource} showSearch={false} disabled={waterFieldsDisabled} />
            </div>
          )}
        </div>
        {isMobile && (
          <div className="actions">
            <div className="right">
              <ResetButton />
            </div>
          </div>
        )}
        <div className="actions">
          {!isMobile && (
            <div className="left">
              <ResetButton />
            </div>
          )}
          <div className="right">
            <Row>
              <CancelButton onCancel={closeDrawer} />
              <span ref={saveButtonAnchorRef}>
                <SaveButton onDone={closeDrawer} testId="water-added-event-save" />
              </span>
            </Row>
          </div>
        </div>
      </Container>
    </Styled>
  );
};
